import React, { useState } from 'react';
import { Container, Typography, TextField, Button, Box, Alert, CircularProgress } from '@mui/material';
import { useForm } from '@formspree/react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import SubjectIcon from '@mui/icons-material/Subject';
import MessageIcon from '@mui/icons-material/Message';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { GlassPaper, fadeInUp, scaleIn } from '../../styles/styledComponents';
import { styled } from '@mui/material/styles';

const MotionBox = motion(Box);

const PageContainer = styled(Container)(({ theme }) => ({
  fontFamily: '"Orbitron", "Noto Sans JP", sans-serif',
  paddingTop: theme.spacing(10),
  paddingBottom: theme.spacing(8),
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 50%, rgba(0, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 0, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 20%, rgba(0, 255, 0, 0.1) 0%, transparent 50%)
    `,
    pointerEvents: 'none',
    zIndex: -1,
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 900,
  background: 'linear-gradient(45deg, #00ffff 0%, #ff00ff 50%, #00ff00 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginBottom: theme.spacing(2),
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  textAlign: 'center',
  filter: 'drop-shadow(0 0 30px rgba(0, 255, 255, 0.5))',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  
  '& .MuiOutlinedInput-root': {
    fontFamily: '"Rajdhani", "Noto Sans JP", sans-serif',
    backgroundColor: 'rgba(37, 37, 48, 0.5)',
    transition: 'all 0.3s ease',
    
    '& fieldset': {
      borderColor: 'rgba(0, 255, 255, 0.3)',
      borderWidth: 2,
      borderRadius: 0,
    },
    
    '&:hover fieldset': {
      borderColor: 'rgba(0, 255, 255, 0.5)',
    },
    
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.neon.cyan,
      boxShadow: `0 0 10px ${theme.palette.neon.cyan}`,
    },
    
    '&.Mui-error fieldset': {
      borderColor: theme.palette.neon.pink,
      boxShadow: `0 0 10px ${theme.palette.neon.pink}`,
    },
  },
  
  '& .MuiInputLabel-root': {
    fontFamily: '"Orbitron", "Noto Sans JP", sans-serif',
    fontWeight: 600,
    letterSpacing: '0.05em',
    
    '&.Mui-focused': {
      color: theme.palette.neon.cyan,
    },
    
    '&.Mui-error': {
      color: theme.palette.neon.pink,
    },
  },
  
  '& .MuiFormHelperText-root': {
    fontFamily: '"Rajdhani", "Noto Sans JP", sans-serif',
    
    '&.Mui-error': {
      color: theme.palette.neon.pink,
      filter: `drop-shadow(0 0 5px ${theme.palette.neon.pink})`,
    },
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #00ffff 0%, #00aaff 100%)',
  color: '#000',
  padding: theme.spacing(1.5, 4),
  borderRadius: 0,
  border: '2px solid #00ffff',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  boxShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    background: 'linear-gradient(45deg, #00ffff 0%, #00aaff 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 0 30px rgba(0, 255, 255, 0.8)',
    borderColor: '#66ffff',
  },
  
  '&:disabled': {
    background: 'rgba(0, 255, 255, 0.3)',
    borderColor: 'rgba(0, 255, 255, 0.3)',
    color: 'rgba(0, 0, 0, 0.5)',
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  borderColor: theme.palette.neon.cyan,
  color: theme.palette.neon.cyan,
  borderWidth: 2,
  borderRadius: 0,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    borderColor: theme.palette.neon.cyan,
    backgroundColor: 'rgba(0, 255, 255, 0.1)',
    transform: 'translateY(-2px)',
    boxShadow: `0 0 20px ${theme.palette.neon.cyan}`,
  },
}));

const SuccessBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  
  '& .success-icon': {
    fontSize: '4rem',
    color: theme.palette.neon.lime,
    filter: `drop-shadow(0 0 20px ${theme.palette.neon.lime})`,
    animation: 'pulse 2s ease-in-out infinite',
  },
  
  '@keyframes pulse': {
    '0%, 100%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.1)' },
  },
}));

const ContactForm = () => {
  const [state, handleSubmit] = useForm("xblgqjgo");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return !value.trim() ? 'お名前を入力してください' : 
               value.length > 50 ? 'お名前は50文字以内で入力してください' : '';
      case 'email':
        return !value.trim() ? 'メールアドレスを入力してください' :
               !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value) ? '有効なメールアドレスを入力してください' : '';
      case 'subject':
        return !value.trim() ? '件名を入力してください' :
               value.length > 100 ? '件名は100文字以内で入力してください' : '';
      case 'message':
        return !value.trim() ? 'お問い合わせ内容を入力してください' :
               value.length > 1000 ? 'お問い合わせ内容は1000文字以内で入力してください' : '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newErrors = {};
    let hasError = false;

    ['name', 'email', 'subject', 'message'].forEach(field => {
      const value = formData.get(field);
      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
        hasError = true;
      }
    });

    setErrors(newErrors);
    setTouched({
      name: true,
      email: true,
      subject: true,
      message: true
    });

    if (!hasError) {
      handleSubmit(e);
    }
  };

  if (state.succeeded) {
    return (
      <PageContainer maxWidth="md">
        <MotionBox
          initial="initial"
          animate="animate"
          exit="exit"
          variants={fadeInUp}
        >
          <Box sx={{ mb: 6 }}>
            <PageTitle variant="h3" component="h1">
              Contact
            </PageTitle>
          </Box>
          
          <GlassPaper
            component={motion.div}
            variants={scaleIn}
            initial="initial"
            animate="animate"
            sx={{ p: 5, mb: 4 }}
          >
            <SuccessBox>
              <CheckCircleIcon className="success-icon" />
              <Typography 
                variant="h5" 
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  fontWeight: 700,
                  color: 'neon.lime',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Message Sent Successfully
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'text.secondary',
                  mb: 4,
                  fontFamily: '"Rajdhani", "Noto Sans JP", sans-serif',
                }}
              >
                お問い合わせを送信しました。ありがとうございます。
              </Typography>
              <BackButton
                component={motion(Link)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                to="/"
                sx={{ mt: 2 }}
              >
                Back to Home
              </BackButton>
            </SuccessBox>
          </GlassPaper>
        </MotionBox>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="md">
      <MotionBox
        initial="initial"
        animate="animate"
        exit="exit"
        variants={fadeInUp}
      >
        <Box sx={{ mb: 6 }}>
          <PageTitle variant="h3" component="h1">
            Contact
          </PageTitle>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: 'text.secondary',
              maxWidth: '600px',
              margin: '0 auto',
              textAlign: 'center',
              fontWeight: 500,
              letterSpacing: '0.05em',
            }}
          >
            お気軽にお問い合わせください
          </Typography>
        </Box>
      
        <GlassPaper
          component={motion.div}
          variants={scaleIn}
          initial="initial"
          animate="animate"
          sx={{ p: 5, mb: 4 }}
        >
          {state.errors && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 4,
                borderRadius: 0,
                border: '2px solid',
                borderColor: 'neon.pink',
                backgroundColor: 'rgba(255, 0, 102, 0.1)',
                color: 'neon.pink',
                '& .MuiAlert-icon': {
                  color: 'neon.pink',
                },
              }}
            >
              送信に失敗しました。もう一度お試しください。
            </Alert>
          )}

          <Box component="form" onSubmit={handleFormSubmit} noValidate>
            <StyledTextField
              required
              fullWidth
              id="name"
              label="Name"
              name="name"
              disabled={state.submitting}
              error={touched.name && !!errors.name}
              helperText={touched.name && errors.name}
              onBlur={handleBlur}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <PersonIcon sx={{ mr: 1, color: 'neon.cyan' }} />
                ),
              }}
            />
            
            <StyledTextField
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              type="email"
              disabled={state.submitting}
              error={touched.email && !!errors.email}
              helperText={touched.email && errors.email}
              onBlur={handleBlur}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <EmailIcon sx={{ mr: 1, color: 'neon.cyan' }} />
                ),
              }}
            />
            
            <StyledTextField
              required
              fullWidth
              id="subject"
              label="Subject"
              name="subject"
              disabled={state.submitting}
              error={touched.subject && !!errors.subject}
              helperText={touched.subject && errors.subject}
              onBlur={handleBlur}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <SubjectIcon sx={{ mr: 1, color: 'neon.cyan' }} />
                ),
              }}
            />
            
            <StyledTextField
              required
              fullWidth
              id="message"
              label="Message"
              name="message"
              multiline
              rows={6}
              disabled={state.submitting}
              error={touched.message && !!errors.message}
              helperText={touched.message && errors.message}
              onBlur={handleBlur}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <MessageIcon sx={{ mr: 1, color: 'neon.cyan', alignSelf: 'flex-start', mt: 1.5 }} />
                ),
              }}
            />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
              <SubmitButton
                component={motion.button}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={state.submitting}
                endIcon={state.submitting ? null : <SendIcon />}
              >
                {state.submitting ? (
                  <CircularProgress size={24} sx={{ color: '#000' }} />
                ) : (
                  'Send Message'
                )}
              </SubmitButton>
              
              <BackButton
                component={motion(Link)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                startIcon={<ArrowBackIcon />}
                to="/"
                variant="outlined"
                fullWidth
              >
                Back to Home
              </BackButton>
            </Box>
          </Box>
        </GlassPaper>
      </MotionBox>
    </PageContainer>
  );
};

export default ContactForm;
